'use client';

import { type KeyboardEvent, type RefObject } from 'react';

import { useTranslations } from 'next-intl';

import { EmojiPickerButton } from './EmojiPickerButton';

function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return true;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

interface PinPopoverCommentBodyProps {
  isEditing: boolean;
  isResolved?: boolean;
  label: string;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  canEdit: boolean;
  onUpdateLabel: (label: string) => void;
  onSave: () => void;
  onStartEditing: () => void;
}

export function PinPopoverCommentBody({
  isEditing,
  isResolved,
  label,
  textareaRef,
  canEdit,
  onUpdateLabel,
  onSave,
  onStartEditing,
}: PinPopoverCommentBodyProps) {
  const t = useTranslations();
  const commentPlaceholder = isMacPlatform()
    ? t('get-feedback-pinComment-placeholderMac')
    : t('get-feedback-pinComment-placeholderWin');

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      onSave();
    }
  }

  return (
    <>
      <div className={`px-3 ${isResolved && !isEditing ? 'pb-1' : 'pb-3'}`}>
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={label}
            onChange={(e) => onUpdateLabel(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={commentPlaceholder}
            rows={3}
            className="w-full resize-none rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
          />
        ) : canEdit ? (
          <p
            role="button"
            tabIndex={0}
            onClick={onStartEditing}
            onKeyDown={(e) => e.key === 'Enter' && onStartEditing()}
            className="min-h-[3rem] cursor-text rounded-md px-2 py-1.5 text-sm text-foreground leading-relaxed hover:bg-muted/50 transition-colors"
          >
            {label || (
              <span className="text-muted-foreground">
                {t('get-feedback-pinComment-emptyHint')}
              </span>
            )}
          </p>
        ) : (
          <p className="min-h-[3rem] rounded-md px-2 py-1.5 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
            {label}
          </p>
        )}
      </div>

      {isResolved && !isEditing && (
        <div
          className="mx-3 mb-4 flex items-center gap-1.5 font-normal text-green-600"
          style={{ fontSize: '11px' }}
        >
          <svg
            width="11"
            height="8"
            viewBox="0 0 11 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-shrink-0"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M1.70711 3.29289C1.31658 2.90237 0.683418 2.90237 0.292893 3.29289C-0.0976311 3.68342 -0.0976311 4.31658 0.292893 4.70711L3.29289 7.70711C3.68342 8.09763 4.31658 8.09763 4.70711 7.70711L10.7071 1.70711C11.0976 1.31658 11.0976 0.683418 10.7071 0.292893C10.3166 -0.0976311 9.68342 -0.0976311 9.29289 0.292893L4 5.58579L1.70711 3.29289Z"
              fill="currentColor"
            />
          </svg>
          {t('get-feedback-status-resolved')}
        </div>
      )}

      {isEditing && (
        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <EmojiPickerButton
            onEmojiSelect={(emoji) => onUpdateLabel(label + emoji)}
          />
          <button
            onClick={onSave}
            className="rounded-[6px] bg-[#151414] px-3 py-1 text-xs font-medium text-white hover:opacity-90 transition-opacity"
          >
            {t('get-feedback-pinComment-save')}
          </button>
        </div>
      )}
    </>
  );
}
